/**
 * ExercisePicker — searchable picker that pulls from the open-source
 * `yuhonas/free-exercise-db` library exposed at `/api/exercises/search`.
 *
 * Props:
 *   value      Array<PrescribedExercise>   currently picked items
 *   onChange   (next) => void              setter
 *   context    "rehab" | "fitness"         drives AI persona on AI Prescribe
 *   patientId  optional — passed to the AI prescribe audit
 *   clientId   optional — passed to the AI prescribe audit
 *   readOnly   bool — render-only mode for patients
 *   testidPrefix string — for stable e2e selectors
 *
 * Each item shape:
 *   { exercise_id, name, sets, reps, rest, notes, primaryMuscles[], level, instructions?[] }
 */
import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Search, Plus, Trash2, Sparkles, X, ExternalLink, Loader2, Dumbbell } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_PRESC = { sets: 3, reps: '10-12', rest: '60s', notes: '' };

export default function ExercisePicker({
  value = [], onChange, context = 'rehab', patientId, clientId,
  readOnly = false, testidPrefix = 'ep',
}) {
  const [showSearch, setShowSearch] = useState(false);
  const [showAi, setShowAi] = useState(false);

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const update = (idx, k, v) => onChange(value.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const add = (ex) => {
    // Don't add duplicates.
    if (value.some(it => it.exercise_id === ex.id)) {
      toast.message(`${ex.name} is already in the list.`);
      return;
    }
    onChange([...value, {
      exercise_id: ex.id,
      name: ex.name,
      primaryMuscles: ex.primaryMuscles || [],
      level: ex.level,
      equipment: ex.equipment,
      ...DEFAULT_PRESC,
    }]);
  };

  return (
    <div data-testid={`${testidPrefix}-root`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="text-sm text-fg-muted">
          {value.length === 0
            ? 'No exercises prescribed yet.'
            : `${value.length} exercise${value.length === 1 ? '' : 's'} prescribed`}
        </p>
        {!readOnly && (
          <div className="flex gap-2">
            <button onClick={() => setShowSearch(true)}
                    className="btn-secondary text-xs inline-flex items-center gap-1"
                    data-testid={`${testidPrefix}-open-search`}>
              <Search className="w-3.5 h-3.5"/> Browse library
            </button>
            <button onClick={() => setShowAi(true)}
                    className="btn-primary text-xs inline-flex items-center gap-1"
                    data-testid={`${testidPrefix}-open-ai`}>
              <Sparkles className="w-3.5 h-3.5"/> AI prescribe
            </button>
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className="surface border border-soft rounded-lg overflow-hidden" data-testid={`${testidPrefix}-table`}>
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary/40">
              <tr className="text-left text-xs uppercase tracking-wider text-fg-muted">
                <th className="px-3 py-2">Exercise</th>
                <th className="px-3 py-2 w-16">Sets</th>
                <th className="px-3 py-2 w-24">Reps</th>
                <th className="px-3 py-2 w-24">Rest</th>
                <th className="px-3 py-2">Notes</th>
                {!readOnly && <th className="w-8"></th>}
              </tr>
            </thead>
            <tbody>
              {value.map((it, i) => (
                <tr key={`${it.exercise_id}-${i}`} className="border-t border-soft" data-testid={`${testidPrefix}-row-${i}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-fg-muted">
                      {(it.primaryMuscles || []).join(' · ') || it.level || '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" className="input !p-1 !text-sm" value={it.sets || ''} readOnly={readOnly}
                           onChange={e => update(i, 'sets', parseInt(e.target.value) || 0)}/>
                  </td>
                  <td className="px-3 py-2">
                    <input className="input !p-1 !text-sm" value={it.reps || ''} readOnly={readOnly}
                           onChange={e => update(i, 'reps', e.target.value)}/>
                  </td>
                  <td className="px-3 py-2">
                    <input className="input !p-1 !text-sm" value={it.rest || ''} readOnly={readOnly}
                           onChange={e => update(i, 'rest', e.target.value)}/>
                  </td>
                  <td className="px-3 py-2">
                    <input className="input !p-1 !text-sm" value={it.notes || ''} readOnly={readOnly}
                           onChange={e => update(i, 'notes', e.target.value)}/>
                  </td>
                  {!readOnly && (
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => remove(i)} className="text-[#B83A3A] hover:opacity-70"
                              title="Remove" data-testid={`${testidPrefix}-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showSearch && <SearchModal onClose={() => setShowSearch(false)} onAdd={add} testidPrefix={testidPrefix}/>}
      {showAi && (
        <AiPrescribeModal onClose={() => setShowAi(false)}
                          onApply={(plan) => { onChange([...value, ...plan]); setShowAi(false); }}
                          context={context} patientId={patientId} clientId={clientId}
                          testidPrefix={testidPrefix}/>
      )}
    </div>
  );
}


// ─── Search modal ────────────────────────────────────────────────────────

function SearchModal({ onClose, onAdd, testidPrefix }) {
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ muscle: '', level: '', equipment: '', category: '' });
  const [meta, setMeta] = useState({ muscles: [], levels: [], equipment: [], categories: [] });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get('/exercises/_meta/filters').then(r => setMeta(r.data)).catch(()=>{}); }, []);

  const runSearch = async () => {
    setBusy(true);
    try {
      const params = { limit: 50 };
      if (q) params.q = q;
      if (filters.muscle) params.muscle = filters.muscle;
      if (filters.level) params.level = filters.level;
      if (filters.equipment) params.equipment = filters.equipment;
      if (filters.category) params.category = filters.category;
      const r = await api.get('/exercises/search', { params });
      setRows(r.data || []);
    } finally { setBusy(false); }
  };

  // First load + when filters change (debounced).
  useEffect(() => {
    const t = setTimeout(runSearch, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, filters.muscle, filters.level, filters.equipment, filters.category]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-bg-primary rounded-lg max-w-5xl w-full p-6 shadow-2xl max-h-[88vh] overflow-hidden flex flex-col" data-testid={`${testidPrefix}-search-modal`}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-heading font-bold inline-flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-brand"/> Exercise library
            </h3>
            <p className="text-xs text-fg-muted">
              873 open-source exercises ·{' '}
              <a href="https://github.com/yuhonas/free-exercise-db" target="_blank" rel="noreferrer"
                 className="underline inline-flex items-center gap-1">
                yuhonas/free-exercise-db <ExternalLink className="w-3 h-3"/>
              </a>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* iter 68 — sticky "Done" in the top-right so users never have to scroll
                to the bottom of the modal to close it. */}
            <button onClick={onClose} className="btn-primary text-sm"
                    data-testid={`${testidPrefix}-done-top`}>Done</button>
            <button onClick={onClose} className="text-fg-muted hover:text-fg" aria-label="Close"><X className="w-4 h-4"/></button>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2 mb-3">
          <input className="input col-span-2" placeholder="Search (e.g. squat, lunge…)" value={q}
                 onChange={e => setQ(e.target.value)} data-testid={`${testidPrefix}-q`}/>
          <select className="input" value={filters.muscle}
                  onChange={e => setFilters({ ...filters, muscle: e.target.value })}
                  data-testid={`${testidPrefix}-muscle`}>
            <option value="">Any muscle</option>
            {meta.muscles.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="input" value={filters.level}
                  onChange={e => setFilters({ ...filters, level: e.target.value })}
                  data-testid={`${testidPrefix}-level`}>
            <option value="">Any level</option>
            {meta.levels.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select className="input" value={filters.equipment}
                  onChange={e => setFilters({ ...filters, equipment: e.target.value })}
                  data-testid={`${testidPrefix}-equipment`}>
            <option value="">Any equipment</option>
            {meta.equipment.map(eq => <option key={eq} value={eq}>{eq}</option>)}
          </select>
        </div>

        <div className="flex-1 overflow-auto border border-soft rounded">
          {busy && (
            <div className="p-6 text-center text-fg-muted text-sm inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="w-4 h-4 animate-spin"/> Searching…
            </div>
          )}
          {!busy && rows.length === 0 && (
            <div className="p-8 text-center text-fg-muted text-sm">No exercises match the filters.</div>
          )}
          {!busy && rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-bg-secondary/40 sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wider text-fg-muted">
                  <th className="px-3 py-2">Exercise</th>
                  <th className="px-3 py-2">Primary muscles</th>
                  <th className="px-3 py-2">Level</th>
                  <th className="px-3 py-2">Equipment</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(ex => (
                  <tr key={ex.id}
                      className="border-t border-soft hover:bg-bg-secondary/30 transition-colors"
                      data-testid={`${testidPrefix}-result-${ex.id}`}>
                    <td className="px-3 py-2">
                      <button onClick={() => setDetail(ex.id)} className="font-medium hover:text-brand underline-offset-2 hover:underline text-left">
                        {ex.name}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-xs text-fg-muted">{(ex.primaryMuscles || []).join(', ')}</td>
                    <td className="px-3 py-2 text-xs">{ex.level || '—'}</td>
                    <td className="px-3 py-2 text-xs">{ex.equipment || '—'}</td>
                    <td className="px-2 py-2">
                      <button onClick={() => onAdd(ex)} className="btn-primary text-xs inline-flex items-center gap-1"
                              data-testid={`${testidPrefix}-add-${ex.id}`}>
                        <Plus className="w-3 h-3"/> Add
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-3">
          <button onClick={onClose} className="btn-secondary text-sm">Done</button>
        </div>

        {detail && <DetailDrawer exerciseId={detail} onClose={() => setDetail(null)} onAdd={onAdd}/>}
      </div>
    </div>
  );
}


function DetailDrawer({ exerciseId, onClose, onAdd }) {
  const [ex, setEx] = useState(null);
  useEffect(() => { api.get(`/exercises/${exerciseId}`).then(r => setEx(r.data)); }, [exerciseId]);
  if (!ex) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-heading font-bold">{ex.name}</h3>
          <button onClick={onClose}><X className="w-4 h-4"/></button>
        </div>
        <div className="text-xs text-fg-muted mb-3">
          {(ex.primaryMuscles || []).join(', ')} · {ex.level} · {ex.equipment || 'body weight'}
        </div>
        {ex.images_resolved && ex.images_resolved[0] && (
          <img src={ex.images_resolved[0]} alt={ex.name} className="rounded-lg w-full max-h-96 object-contain bg-black/5 mb-3"/>
        )}
        {ex.instructions && ex.instructions.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold mb-2">Instructions</h4>
            <ol className="list-decimal ml-5 text-sm space-y-1">
              {ex.instructions.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-4">
          <button onClick={onClose} className="btn-secondary text-sm">Close</button>
          <button onClick={() => { onAdd(ex); onClose(); }} className="btn-primary text-sm inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5"/> Add to program
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── AI prescribe modal ─────────────────────────────────────────────────

function AiPrescribeModal({ onClose, onApply, context, patientId, clientId, testidPrefix }) {
  const [goal, setGoal] = useState('');
  const [level, setLevel] = useState('beginner');
  const [muscles, setMuscles] = useState('');
  const [equipment, setEquipment] = useState('');
  const [duration, setDuration] = useState(30);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!goal || goal.length < 4) return toast.error('Describe the goal in at least a few words.');
    setBusy(true);
    setResult(null);
    try {
      const body = {
        goal, level,
        target_muscles: muscles ? muscles.split(',').map(s => s.trim()).filter(Boolean) : undefined,
        equipment: equipment || undefined,
        duration_min: duration,
        context,
        patient_id: patientId,
        client_id: clientId,
      };
      const r = await api.post('/exercises/prescribe', body);
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI prescription failed.');
    } finally { setBusy(false); }
  };

  const apply = () => {
    if (!result?.plan) return;
    const items = result.plan.map(p => ({
      exercise_id: p.exercise?.id || p.exercise_id,
      name: p.exercise?.name || '',
      primaryMuscles: p.exercise?.primaryMuscles || [],
      level: p.exercise?.level,
      equipment: p.exercise?.equipment,
      sets: p.sets || 3,
      reps: p.reps || '10-12',
      rest: p.rest || '60s',
      notes: p.rationale || '',
    })).filter(it => it.exercise_id);
    onApply(items);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testidPrefix}-ai-modal`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-bold inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand"/> AI exercise prescription
          </h3>
          <button onClick={onClose}><X className="w-4 h-4"/></button>
        </div>

        <p className="text-xs text-fg-muted mb-3">
          Picks 4-6 exercises from the open-source library, scaled to the client's level.
          Exercises come from the dataset — the AI never invents movements.
        </p>

        <label className="block mb-3">
          <div className="text-xs text-fg-muted mb-1">Goal</div>
          <textarea className="input min-h-[60px]" value={goal} onChange={e => setGoal(e.target.value)}
                    placeholder={context === 'rehab'
                      ? 'e.g. rehab right knee post-ACL, restore quad strength, gait normalisation'
                      : 'e.g. lose body fat, build core strength, prepare for 5K run'}
                    data-testid={`${testidPrefix}-ai-goal`}/>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block mb-3">
            <div className="text-xs text-fg-muted mb-1">Level</div>
            <select className="input" value={level} onChange={e => setLevel(e.target.value)}
                    data-testid={`${testidPrefix}-ai-level`}>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="expert">Expert</option>
            </select>
          </label>
          <label className="block mb-3">
            <div className="text-xs text-fg-muted mb-1">Duration (min)</div>
            <select className="input" value={duration} onChange={e => setDuration(parseInt(e.target.value))}
                    data-testid={`${testidPrefix}-ai-duration`}>
              {[10,15,20,25,30,35,40,45,50,55,60].map(m => (
                <option key={m} value={m}>{m} min</option>
              ))}
            </select>
          </label>
        </div>

        <label className="block mb-3">
          <div className="text-xs text-fg-muted mb-1">Target muscles (comma-separated, optional)</div>
          <input className="input" value={muscles} onChange={e => setMuscles(e.target.value)}
                 placeholder="quadriceps, glutes, hamstrings"
                 data-testid={`${testidPrefix}-ai-muscles`}/>
        </label>

        <label className="block mb-3">
          <div className="text-xs text-fg-muted mb-1">Equipment (optional)</div>
          <input className="input" value={equipment} onChange={e => setEquipment(e.target.value)}
                 placeholder="dumbbell, body only, …"/>
        </label>

        {!result && (
          <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-4">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={run} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid={`${testidPrefix}-ai-run`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
              {busy ? 'Drafting…' : 'Generate prescription'}
            </button>
          </div>
        )}

        {result && (
          <div className="mt-4 border-t border-soft pt-4">
            {result.summary && <p className="text-sm mb-3 italic text-fg-muted">"{result.summary}"</p>}
            <div className="space-y-2">
              {result.plan.map((p, i) => (
                <div key={i} className="surface border border-soft rounded p-3 text-sm" data-testid={`${testidPrefix}-ai-row-${i}`}>
                  <div className="font-medium">{p.exercise?.name}</div>
                  <div className="text-xs text-fg-muted">
                    {(p.exercise?.primaryMuscles || []).join(', ')} · {p.sets}×{p.reps} · rest {p.rest}
                  </div>
                  {p.rationale && <div className="text-xs text-fg-secondary mt-1">{p.rationale}</div>}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-soft">
              <button onClick={() => setResult(null)} className="btn-secondary text-sm">Refine</button>
              <button onClick={apply} className="btn-primary text-sm" data-testid={`${testidPrefix}-ai-apply`}>
                Add all to program
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
